import type { IntakeSummaryPdfData } from '@common/types/formSubmissions';
import type { ProspectResponse } from '@common/types/prospects';
import { useQuery } from '@tanstack/react-query';
import FullScreenLoader from '@/components/FullScreenLoader';
import { getIntakeFormResponse } from '@/services/ProspectService';
import { isAxiosNotFoundError } from '@/utils/queryError';
import ProspectDetailsEmptyPage from '../ProspectDetailsEmptyPage';
import ProspectDetailsErrorPage from '../ProspectDetailsErrorPage';
import IntakeResponseInformationView from './IntakeResponseInformationView';

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
    selectedVersionId: number | null;
}

export default function IntakeResponseInformationV2({
    prospectId,
    prospect: prospectDetails,
    selectedVersionId,
}: Props) {
    const {
        data = {
            summaryData: {
                matterType: prospectDetails.matterType,
                formSubmissionFields: [],
                contactFormValues: [],
            },
        },
        isPending,
        isError,
        isFetching,
        error,
        refetch,
    } = useQuery<{
        summaryData: IntakeSummaryPdfData;
        isDraft?: boolean;
        draftLastUpdatedAt?: Date | string;
    }>({
        queryKey: ['intake-response-information-v2', prospectId, selectedVersionId],
        queryFn: async () => {
            if (!prospectId) {
                return {
                    summaryData: {
                        matterType: prospectDetails.matterType,
                        formSubmissionFields: [],
                        contactFormValues: [],
                    },
                };
            }

            const response = await getIntakeFormResponse(prospectId, selectedVersionId);

            return {
                summaryData: {
                    matterType: prospectDetails.matterType,
                    formSubmissionFields: response.data.formSubmissionFields ?? [],
                    contactFormValues: response.data.contactFormValues ?? [],
                },
                isDraft: response.data.isDraft ?? false,
                draftLastUpdatedAt: response.data.draftLastUpdatedAt,
            };
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });

    if (isPending) {
        return <FullScreenLoader />;
    }

    if (isError && isAxiosNotFoundError(error)) {
        return <ProspectDetailsEmptyPage message="Intake response not received from client yet" />;
    }

    if (isError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching the intake form response"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    return (
        <IntakeResponseInformationView
            {...data.summaryData}
            isDraft={data.isDraft}
            draftLastUpdatedAt={data.draftLastUpdatedAt}
            showEmptyAnswersInIntakeSummary={
                prospectDetails.lawFirm.showEmptyAnswersInIntakeSummary ?? true
            }
            showContactsAsColumnsInIntakeSummary={
                prospectDetails.lawFirm.showContactsAsColumnsInIntakeSummary ?? false
            }
        />
    );
}
